import { CampaignType, VoucherValidityMode } from "@/@types/gamification";

import {
  CAMPAIGN_TYPE_PREFIX,
  PRO015_DESCRIPTION_MAX,
  maxCampaignNameLength,
} from "../constants/campaign-name";
import { FIXED_PRIZE_ROW_COUNT } from "../constants/mechanic-defaults";
import { MECHANIC_RULE_MESSAGES } from "../constants/messages";
import { CAMPAIGN_PRIORITY } from "../constants/priority-levels";
import { isPastDate, isStartDateBeforeEndDate } from "@/utils/date-string";

export interface RuleIssue {
  path: (string | number)[];
  message: string;
}

interface PrizeRowLike {
  probability?: number | null;
}

interface SlotSymbolLike {
  id?: string;
  label?: string;
}

interface MechanicConfigLike {
  prizes?: PrizeRowLike[];
  maxPlays?: number;
  minPurchase?: number;
  priority?: number;
  concurrency?: { competes?: boolean; group?: string | null };
  voucherValidity?: {
    mode?: VoucherValidityMode;
    validityDays?: number;
    startOffsetDays?: number;
  };
  cpfSegmentation?: { enabled?: boolean };
  mechanicParams?: {
    goalProbability?: number;
    symbols?: SlotSymbolLike[];
  };
}

export interface MechanicRuleValues {
  campaignType?: CampaignType;
  campaignName?: string;
  startDate?: string;
  endDate?: string;
  mechanicConfig?: MechanicConfigLike;
  segmentationFile?: unknown;

  segmentationCpfs?: string[];
  faqItems?: Array<{ question?: string; answer?: string }>;
}

export const issuePathToString = (path: (string | number)[]): string =>
  path.join(".");

const mechanicOf = (values: MechanicRuleValues): CampaignType =>
  values.campaignType ?? CampaignType.ROULETTE;

export function prizesSumIsValid(
  prizes: Array<{ probability?: number | null }> | undefined,
): boolean {
  const total = (prizes ?? []).reduce(
    (sum, prize) => sum + (Number(prize?.probability) || 0),
    0,
  );
  return Math.abs(total - 100) < 0.01;
}

export function nameLengthRule(values: MechanicRuleValues): RuleIssue | null {
  const mechanic = mechanicOf(values);
  const nameLimit = maxCampaignNameLength(mechanic);
  if ((values.campaignName ?? "").length <= nameLimit) return null;
  return {
    path: ["campaignName"],
    message: MECHANIC_RULE_MESSAGES.namePrefixLimit(
      CAMPAIGN_TYPE_PREFIX[mechanic],
      nameLimit,
      PRO015_DESCRIPTION_MAX,
    ),
  };
}

export function datesOrderRule(values: MechanicRuleValues): RuleIssue | null {
  if (!values.startDate || !values.endDate) return null;
  if (isStartDateBeforeEndDate(values.startDate, values.endDate)) return null;
  return { path: ["endDate"], message: MECHANIC_RULE_MESSAGES.dateOrder };
}

export function startDatePastRule(values: MechanicRuleValues): RuleIssue | null {
  if (!values.startDate) return null;
  if (!isPastDate(values.startDate)) return null;
  return { path: ["startDate"], message: MECHANIC_RULE_MESSAGES.startDatePast };
}

export function prizesRule(values: MechanicRuleValues): RuleIssue | null {
  const mechanic = mechanicOf(values);
  const rows = values.mechanicConfig?.prizes ?? [];
  const path = ["mechanicConfig", "prizes"];

  const fixedCount = FIXED_PRIZE_ROW_COUNT[mechanic];
  if (fixedCount !== null && rows.length !== fixedCount) {
    return { path, message: MECHANIC_RULE_MESSAGES.prizesFixedCount(fixedCount) };
  }
  if (rows.length < 1) {
    return { path, message: MECHANIC_RULE_MESSAGES.prizesMin };
  }
  if (!prizesSumIsValid(rows)) {
    return { path, message: MECHANIC_RULE_MESSAGES.prizesSum };
  }
  return null;
}

export function concurrencyRule(values: MechanicRuleValues): RuleIssue | null {
  const concurrency = values.mechanicConfig?.concurrency;
  if (!concurrency?.competes) return null;
  if ((concurrency.group ?? "").trim()) return null;
  return {
    path: ["mechanicConfig", "concurrency", "group"],
    message: MECHANIC_RULE_MESSAGES.concurrencyGroup,
  };
}

export function voucherValidityRules(values: MechanicRuleValues): RuleIssue[] {
  const validity = values.mechanicConfig?.voucherValidity;
  const mode = validity?.mode ?? VoucherValidityMode.DAYS;
  const issues: RuleIssue[] = [];
  if (mode === VoucherValidityMode.DAYS && !(Number(validity?.validityDays) >= 1)) {
    issues.push({
      path: ["mechanicConfig", "voucherValidity", "validityDays"],
      message: MECHANIC_RULE_MESSAGES.voucherDays,
    });
  }
  if (validity?.startOffsetDays !== undefined && validity.startOffsetDays < 0) {
    issues.push({
      path: ["mechanicConfig", "voucherValidity", "startOffsetDays"],
      message: MECHANIC_RULE_MESSAGES.voucherStartOffset,
    });
  }
  return issues;
}

function penaltyRule(values: MechanicRuleValues): RuleIssue | null {
  const goal = values.mechanicConfig?.mechanicParams?.goalProbability;
  if (goal !== undefined && goal >= 0 && goal <= 1) return null;
  return {
    path: ["mechanicConfig", "mechanicParams", "goalProbability"],
    message: MECHANIC_RULE_MESSAGES.penaltyGoal,
  };
}

function slotRule(values: MechanicRuleValues): RuleIssue | null {
  const symbols = values.mechanicConfig?.mechanicParams?.symbols ?? [];
  const path = ["mechanicConfig", "mechanicParams", "symbols"];
  if (symbols.length < 1) {
    return { path, message: MECHANIC_RULE_MESSAGES.slotSymbols };
  }

  const hasBlank = symbols.some(
    (symbol) => !symbol?.id?.trim() || !symbol?.label?.trim(),
  );
  if (hasBlank) {
    return { path, message: MECHANIC_RULE_MESSAGES.slotSymbolFields };
  }
  return null;
}

const MAX_PLAYS_CEILING = 999;

const isPriorityLevel = (value: number): boolean =>
  Number.isInteger(value) &&
  value >= CAMPAIGN_PRIORITY.LOW &&
  value <= CAMPAIGN_PRIORITY.MAX;

function toNumberOrUndefined(value: unknown): number | undefined {
  if (value === "" || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function numericBoundsRules(values: MechanicRuleValues): RuleIssue[] {
  const config = values.mechanicConfig;
  const issues: RuleIssue[] = [];

  const maxPlays = toNumberOrUndefined(config?.maxPlays);
  if (
    maxPlays !== undefined &&
    (!Number.isInteger(maxPlays) || maxPlays < 1 || maxPlays > MAX_PLAYS_CEILING)
  ) {
    issues.push({
      path: ["mechanicConfig", "maxPlays"],
      message: MECHANIC_RULE_MESSAGES.maxPlaysMin,
    });
  }

  const minPurchase = toNumberOrUndefined(config?.minPurchase);
  if (minPurchase !== undefined && minPurchase < 0) {
    issues.push({
      path: ["mechanicConfig", "minPurchase"],
      message: MECHANIC_RULE_MESSAGES.minPurchaseMin,
    });
  }

  const priority = toNumberOrUndefined(config?.priority);
  if (priority !== undefined && !isPriorityLevel(priority)) {
    issues.push({
      path: ["mechanicConfig", "priority"],
      message: MECHANIC_RULE_MESSAGES.priorityRange,
    });
  }

  return issues;
}

const MECHANIC_PARAM_RULES: Partial<
  Record<CampaignType, (v: MechanicRuleValues) => RuleIssue | null>
> = {
  [CampaignType.PENALTY]: penaltyRule,
  [CampaignType.SLOT]: slotRule,
};

export function mechanicParamsRule(values: MechanicRuleValues): RuleIssue | null {
  return MECHANIC_PARAM_RULES[mechanicOf(values)]?.(values) ?? null;
}

export function segmentationFileRule(
  values: MechanicRuleValues,
): RuleIssue | null {
  if (!values.mechanicConfig?.cpfSegmentation?.enabled) return null;
  if ((values.segmentationCpfs ?? []).length > 0) return null;
  return {
    path: ["segmentationFile"],
    message: MECHANIC_RULE_MESSAGES.segmentationFile,
  };
}

export function faqItemsRule(values: MechanicRuleValues): RuleIssue | null {
  for (const item of values.faqItems ?? []) {
    const question = item?.question?.trim();
    const answer = item?.answer?.trim();
    if (!question || !answer) {
      return { path: ["faqItems"], message: MECHANIC_RULE_MESSAGES.faqPair };
    }
  }
  return null;
}

export function firstIssue(
  values: MechanicRuleValues,
  rules: Array<(v: MechanicRuleValues) => RuleIssue | null>,
): RuleIssue | null {
  for (const rule of rules) {
    const issue = rule(values);
    if (issue) return issue;
  }
  return null;
}

export function configRule(values: MechanicRuleValues): RuleIssue | null {
  const mechanicError = mechanicParamsRule(values);
  if (mechanicError) return mechanicError;
  return firstIssue(values, [
    (v) => numericBoundsRules(v)[0] ?? null,
    concurrencyRule,
    (v) => voucherValidityRules(v)[0] ?? null,
  ]);
}

export function collectMechanicRefineIssues(
  values: MechanicRuleValues,
): RuleIssue[] {
  const issues: RuleIssue[] = [];
  const push = (issue: RuleIssue | null) => {
    if (issue) issues.push(issue);
  };

  push(nameLengthRule(values));
  push(prizesRule(values));
  push(concurrencyRule(values));
  issues.push(...voucherValidityRules(values));
  issues.push(...numericBoundsRules(values));
  push(mechanicParamsRule(values));

  return issues;
}
